import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'gray' | 'dark' | 'midnight' | 'sepia' | 'contrast' | 'amoled' | 'neon' | 'glass' | 'glass-dark';

export const THEMES = [
  { id: 'light', name: 'Light', icon: 'Sun' },
  { id: 'gray', name: 'Gray', icon: 'Monitor' },
  { id: 'dark', name: 'Dark', icon: 'Moon' },
  { id: 'midnight', name: 'Midnight', icon: 'Moon' },
  { id: 'sepia', name: 'Sepia', icon: 'Sun' },
  { id: 'contrast', name: 'Contrast', icon: 'Contrast' },
  { id: 'amoled', name: 'AMOLED', icon: 'Smartphone' },
  { id: 'neon', name: 'Neon', icon: 'Zap' },
  { id: 'glass', name: 'Glass', icon: 'GlassWater' },
  { id: 'glass-dark', name: 'Glass Dark', icon: 'GlassWater' },
] as const;

// Preset accent colors with HSL values
export const ACCENT_COLORS = [
  { name: 'Blue', hsl: '220 70% 50%', hslDark: '220 70% 55%' },
  { name: 'Purple', hsl: '270 70% 50%', hslDark: '270 70% 60%' },
  { name: 'Pink', hsl: '330 70% 50%', hslDark: '330 70% 60%' },
  { name: 'Red', hsl: '0 70% 50%', hslDark: '0 70% 55%' },
  { name: 'Orange', hsl: '25 90% 50%', hslDark: '25 90% 55%' },
  { name: 'Green', hsl: '150 60% 40%', hslDark: '150 60% 45%' },
  { name: 'Teal', hsl: '180 60% 40%', hslDark: '180 60% 45%' },
  { name: 'Cyan', hsl: '195 80% 45%', hslDark: '195 80% 50%' },
] as const;

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  accentColor: string;
  setAccentColor: (color: string) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const VALID_THEMES: Theme[] = ['light', 'gray', 'dark', 'midnight', 'sepia', 'contrast', 'amoled', 'neon', 'glass', 'glass-dark'];

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem('theme') as Theme;
    if (stored && VALID_THEMES.includes(stored)) {
      return stored;
    }
    return 'light';
  });

  const [accentColor, setAccentColor] = useState<string>(() => {
    return localStorage.getItem('accentColor') || 'Blue';
  });

  useEffect(() => {
    const root = document.documentElement;
    
    // Remove all theme classes
    VALID_THEMES.forEach(t => root.classList.remove(t));
    
    // Add current theme class
    root.classList.add(theme);
    
    // Store preference
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const root = document.documentElement;
    const color = ACCENT_COLORS.find(c => c.name === accentColor) || ACCENT_COLORS[0];
    
    // Set accent color CSS variables
    const isDarkTheme = ['dark', 'midnight', 'amoled', 'neon', 'glass-dark'].includes(theme);
    const hsl = isDarkTheme ? color.hslDark : color.hsl;
    
    root.style.setProperty('--primary', hsl);
    root.style.setProperty('--ring', hsl);
    root.style.setProperty('--selection', hsl);
    root.style.setProperty('--sidebar-primary', hsl);
    root.style.setProperty('--sidebar-ring', hsl);
    root.style.setProperty('--element-selected', hsl);
    
    localStorage.setItem('accentColor', accentColor);
  }, [accentColor, theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, accentColor, setAccentColor }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
